
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createEditor, Descendant, Editor, Element as SlateElement, Text, BaseEditor } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory, HistoryEditor } from 'slate-history';
import { Button } from '../ui/button';
import { Bold, Italic, List, Heading2, Underline as UnderlineIcon } from 'lucide-react';
import { useToast } from "../../hooks/use-toast";
import DOMPurify from 'dompurify';

// Define custom types for the editor
type CustomEditor = BaseEditor & ReactEditor & HistoryEditor;

interface RichTextEditorProps {
  initialContent: string;
  onChange: (content: string) => void;
  placeholder?: string;
  onBlur?: () => void;
  showToast?: boolean;
}

// Define precisely typed slate elements
type ParagraphElement = {
  type: 'paragraph';
  children: CustomText[];
};

type HeadingTwoElement = {
  type: 'heading-two';
  children: CustomText[];
};

type BulletedListElement = {
  type: 'bulleted-list';
  children: CustomText[];
};

type ListItemElement = {
  type: 'list-item';
  children: CustomText[];
};

type CustomElement = ParagraphElement | HeadingTwoElement | BulletedListElement | ListItemElement;

type CustomText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
};

// Create default empty paragraph for Slate editor
const emptyParagraph: ParagraphElement = {
  type: 'paragraph',
  children: [{ text: '' }]
};

// Default initial value for the editor
const defaultInitialValue: CustomElement[] = [emptyParagraph];

// Type declarations for Slate
declare module 'slate' {
  interface CustomTypes {
    Editor: CustomEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

// Safely check if content is empty
const isContentEmpty = (html: string): boolean => {
  if (!html) return true;
  const textContent = (html.replace(/<[^>]*>/g, '') || '').trim();
  return textContent === '';
};

// Convert HTML to Slate nodes with improved error handling
const htmlToSlate = (html: string): CustomElement[] => {
  try {
    if (!html || isContentEmpty(html)) {
      return defaultInitialValue;
    }

    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(html);
    
    const slateNodes: CustomElement[] = [];
    
    // Process each child node
    Array.from(tempDiv.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Handle plain text nodes
        if (node.textContent?.trim()) {
          slateNodes.push({ 
            type: 'paragraph', 
            children: [{ text: node.textContent || '' }] 
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        
        // Convert different HTML elements to their Slate equivalents
        switch (element.tagName.toLowerCase()) {
          case 'h2':
            slateNodes.push({
              type: 'heading-two',
              children: [{ text: element.textContent || '' }]
            });
            break;
          case 'p':
            const children: CustomText[] = [];
            
            // Handle text formatting within paragraphs
            Array.from(element.childNodes).forEach(child => {
              if (child.nodeType === Node.TEXT_NODE) {
                children.push({ text: child.textContent || '' });
              } else if (child.nodeType === Node.ELEMENT_NODE) {
                const childElement = child as HTMLElement;
                const text = childElement.textContent || '';
                const format: Partial<CustomText> = { text };
                
                switch (childElement.tagName.toLowerCase()) {
                  case 'strong':
                  case 'b':
                    format.bold = true;
                    break;
                  case 'em':
                  case 'i':
                    format.italic = true;
                    break;
                  case 'u':
                    format.underline = true;
                    break;
                }
                
                children.push(format as CustomText);
              }
            });
            
            // Ensure paragraphs always have at least one child
            if (children.length === 0) {
              children.push({ text: element.textContent || '' });
            }
            
            slateNodes.push({
              type: 'paragraph',
              children
            });
            break;
          case 'ul':
            // Process list items
            Array.from(element.querySelectorAll('li')).forEach(li => {
              slateNodes.push({
                type: 'bulleted-list',
                children: [{ text: li.textContent || '' }]
              });
            });
            break;
          case 'li':
            slateNodes.push({
              type: 'list-item',
              children: [{ text: element.textContent || '' }]
            });
            break;
          default:
            // Default to paragraph for unknown elements
            slateNodes.push({
              type: 'paragraph',
              children: [{ text: element.textContent || '' }]
            });
        }
      }
    });
    
    // Always ensure at least one node exists
    if (slateNodes.length === 0) {
      return defaultInitialValue;
    }
    
    return slateNodes;
  } catch (error) {
    console.error('Error converting HTML to Slate:', error);
    return defaultInitialValue;
  }
};

// Convert Slate nodes to HTML string
const slateToHtml = (nodes: Descendant[]): string => {
  try {
    if (!nodes || nodes.length === 0) {
      return '';
    }
    
    let html = '';
    
    nodes.forEach(node => {
      if (!SlateElement.isElement(node)) return;
      
      const textContent = node.children
        .map(child => {
          if (!Text.isText(child)) return '';
          
          let text = child.text || '';
          
          // Apply text formatting
          if (child.bold) {
            text = `<strong>${text}</strong>`;
          }
          if (child.italic) {
            text = `<em>${text}</em>`;
          }
          if (child.underline) {
            text = `<u>${text}</u>`;
          }
          
          return text;
        })
        .join('');
      
      // Convert Slate node types to HTML elements
      switch (node.type) {
        case 'heading-two':
          html += `<h2 class="text-xl font-semibold">${textContent}</h2>`;
          break;
        case 'paragraph':
          html += `<p>${textContent}</p>`;
          break;
        case 'bulleted-list':
          html += `<ul><li>${textContent}</li></ul>`;
          break;
        case 'list-item':
          html += `<li>${textContent}</li>`;
          break;
        default:
          html += `<p>${textContent}</p>`;
      }
    });
    
    return html;
  } catch (error) {
    console.error('Error converting Slate to HTML:', error);
    return '<p>Error formatting content</p>';
  }
};

// Toolbar button component
const ToolbarButton = ({ 
  format, 
  icon, 
  active, 
  onMouseDown 
}: {
  format: string;
  icon: React.ReactNode;
  active: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
}) => {
  return (
    <Button
      variant="ghost"
      size="sm"
      onMouseDown={onMouseDown}
      className={active ? 'bg-muted' : ''}
    >
      {icon}
    </Button>
  );
};

// Check if mark is active
const isMarkActive = (editor: CustomEditor, format: keyof Omit<CustomText, 'text'>) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

// Check if block is active
const isBlockActive = (editor: CustomEditor, format: string) => {
  const { selection } = editor;
  if (!selection) return false;

  try {
    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: n => 
          !Editor.isEditor(n) && 
          SlateElement.isElement(n) && 
          n.type === format,
      })
    );
    return !!match;
  } catch (error) {
    console.error('Error checking if block is active:', error);
    return false;
  }
};

// Toggle mark
const toggleMark = (editor: CustomEditor, format: keyof Omit<CustomText, 'text'>) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

// Toggle block
const toggleBlock = (editor: CustomEditor, format: string) => {
  try {
    const isActive = isBlockActive(editor, format);
    
    Editor.setNodes(
      editor,
      { type: isActive ? 'paragraph' : format },
      { match: n => SlateElement.isElement(n) && Editor.isBlock(editor, n) }
    );
  } catch (error) {
    console.error('Error toggling block:', error);
  }
};

// Custom Element component for rendering different node types
const Element = ({ attributes, children, element }: {
  attributes: any;
  children: React.ReactNode;
  element: CustomElement;
}) => {
  switch (element.type) {
    case 'heading-two':
      return <h2 {...attributes} className="text-xl font-semibold">{children}</h2>;
    case 'bulleted-list':
      return <li {...attributes}>{children}</li>;
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    default:
      return <p {...attributes}>{children}</p>;
  }
};

// Custom Leaf component for rendering text with marks
const Leaf = ({ attributes, children, leaf }: {
  attributes: any;
  children: React.ReactNode;
  leaf: CustomText;
}) => {
  let formattedChildren = children;
  
  if (leaf.bold) {
    formattedChildren = <strong>{formattedChildren}</strong>;
  }
  
  if (leaf.italic) {
    formattedChildren = <em>{formattedChildren}</em>;
  }
  
  if (leaf.underline) {
    formattedChildren = <u>{formattedChildren}</u>;
  }
  
  return <span {...attributes}>{formattedChildren}</span>;
};

export function RichTextEditor({ 
  initialContent, 
  onChange, 
  placeholder, 
  onBlur,
  showToast = false 
}: RichTextEditorProps) {
  // Create a Slate editor object that won't change across renders
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const { toast } = useToast();
  const editorRef = useRef<HTMLDivElement>(null);
  
  // Initialize with safe default content
  const [value, setValue] = useState<Descendant[]>(() => {
    try {
      const safeContent = typeof initialContent === 'string' && initialContent ? initialContent : '';
      return htmlToSlate(safeContent);
    } catch (error) {
      console.error('Failed to parse initial content:', error);
      return defaultInitialValue;
    }
  });
  
  // Track editing state for content updates
  const [isEditing, setIsEditing] = useState(false);
  
  // Update editor value when initialContent changes but only if not editing
  useEffect(() => {
    if (!isEditing && initialContent !== undefined) {
      try {
        const safeContent = typeof initialContent === 'string' ? initialContent : '';
        const parsedValue = htmlToSlate(safeContent);
        setValue(parsedValue);
      } catch (error) {
        console.error('Error updating editor content:', error);
      }
    }
  }, [initialContent, isEditing]);

  // Handle editor value changes
  const handleChange = useCallback((newValue: Descendant[]) => {
    // Set editing state to track user modifications
    if (!isEditing) {
      setIsEditing(true);
    }
    
    // Update local state
    setValue(newValue);
    
    try {
      // Convert to HTML and inform parent component
      const html = slateToHtml(newValue);
      onChange(html);
    } catch (error) {
      console.error('Error converting to HTML:', error);
    }
  }, [onChange, isEditing]);

  // Handle blur events
  const handleBlur = useCallback(() => {
    // Reset editing state
    setIsEditing(false);
    
    // Trigger parent onBlur handler if provided
    if (onBlur) {
      onBlur();
      
      // Show success toast if requested
      if (showToast) {
        toast({
          title: "Success",
          description: "Content saved successfully",
        });
      }
    }
  }, [onBlur, showToast, toast]);

  // Toolbar component
  const Toolbar = () => {
    return (
      <div className="border-b p-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
        <ToolbarButton 
          format="bold"
          icon={<Bold className="h-4 w-4" />}
          active={isMarkActive(editor, 'bold')}
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault();
            toggleMark(editor, 'bold');
          }}
        />
        <ToolbarButton 
          format="italic"
          icon={<Italic className="h-4 w-4" />}
          active={isMarkActive(editor, 'italic')}
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault();
            toggleMark(editor, 'italic');
          }}
        />
        <ToolbarButton 
          format="underline"
          icon={<UnderlineIcon className="h-4 w-4" />}
          active={isMarkActive(editor, 'underline')}
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault();
            toggleMark(editor, 'underline');
          }}
        />
        <ToolbarButton 
          format="heading-two"
          icon={<Heading2 className="h-4 w-4" />}
          active={isBlockActive(editor, 'heading-two')}
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault();
            toggleBlock(editor, 'heading-two');
          }}
        />
        <ToolbarButton 
          format="bulleted-list"
          icon={<List className="h-4 w-4" />}
          active={isBlockActive(editor, 'bulleted-list')}
          onMouseDown={(e: React.MouseEvent) => {
            e.preventDefault();
            toggleBlock(editor, 'bulleted-list');
          }}
        />
      </div>
    );
  };

  return (
    <div 
      className="border rounded-md bg-background" 
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      ref={editorRef}
    >
      <Slate
        editor={editor}
        initialValue={value}
        onChange={handleChange}
      >
        <Toolbar />
        <div className="prose prose-sm max-w-none dark:prose-invert focus:outline-none min-h-[120px] p-4">
          <Editable
            renderElement={props => <Element {...props} />}
            renderLeaf={props => <Leaf {...props} />}
            placeholder={placeholder || "Enter content..."}
            onBlur={handleBlur}
            onFocus={() => setIsEditing(true)}
            spellCheck
          />
        </div>
      </Slate>
    </div>
  );
}
